import { useState, useCallback, useEffect } from 'react';
import { TextCollectionRankingOrderOption } from '@scale/shared/types/params/textcollection';
import {
  ItemType,
  IUnlabeledItem,
  ILabeledItem,
  IPlaceholderItem,
  IRankingOrderData,
  ILabelOrPlaceholderItems,
  ITiedItems,
} from './types';
import uuid from 'uuid';
import _ from 'lodash';
import { assertDefined } from '@scale/shared/types/utils';
import { RankingOrderFieldAnnotation } from '@scale/shared/types/response/textcollection';
import { useRLHFHackStore } from 'react-scale/components/scalers/tasks/textcollection/stores/rlhfHackStore';

export const useRankingOrderData = (
  _rankingOrderOptions: TextCollectionRankingOrderOption[],
  numOptions: number,
  numPositions: number,
  maxTiedItems: number,
  optionLabel: string,
  annotation: RankingOrderFieldAnnotation,
  setAnnotation?: (annotation: RankingOrderFieldAnnotation) => void,
  itemsToRankCandidates?: number[],
  isReviewMode?: boolean,
) => {
  const initialData = _getInitialRankingOrderData(
    numOptions,
    numPositions,
    maxTiedItems,
    optionLabel,
    annotation,
    itemsToRankCandidates,
    isReviewMode,
  );

  const [data, setData] = useState<IRankingOrderData>(initialData);
  const [prevItemsToRankCandidates, setItemsToRankCandidates] = useState(itemsToRankCandidates);
  if (
    itemsToRankCandidates &&
    JSON.stringify(itemsToRankCandidates) !== JSON.stringify(prevItemsToRankCandidates)
  ) {
    setItemsToRankCandidates(itemsToRankCandidates);
    setData(initialData);
  }

  const { reviewOrderMapping } = useRLHFHackStore(state => ({
    reviewOrderMapping: state.reviewOrderMapping,
  }));

  useEffect(() => {
    if (!setAnnotation) return;
    const sortedMapping = reviewOrderMapping
      ? [...reviewOrderMapping].sort((a, b) => a.to - b.to)
      : undefined;
    const newResponse = toAnnotationResponse(
      data,
      sortedMapping?.map(s => s.from),
    );

    if (!_.isEqual(newResponse, annotation.response)) {
      setAnnotation({ ...annotation, response: newResponse });
    }
  }, [annotation, setAnnotation, data]);

  const onDropToLabeledOrPlaceholderItem = useCallback(
    (dragItem: IUnlabeledItem | ILabeledItem, dropItem: ILabeledItem | IPlaceholderItem) => {
      // Unlabel item to placeholder
      if (
        dragItem.dndType === ItemType.UnlabeledItem &&
        dropItem.dndType === ItemType.PlaceholderItem
      ) {
        setData(prevData => {
          const newData = _.cloneDeep(prevData);
          // 1. Remove unlabeled item
          _.remove(newData.unlabeledItems, item => item.id === dragItem.id);

          // 2. Create new labeled item
          const newLabeledItem: ILabeledItem = {
            ...dragItem,
            dndType: ItemType.LabeledItem,
          };

          // 3. Add to the labeledOrPlaceholderItems array and remove the placeholder
          const tiedItemsIndex = _.findIndex(newData.labeledOrPlaceholderItems, tiedItems => {
            return !!_.find(tiedItems, tiedItem => tiedItem.id === dropItem.id);
          });

          const newTiedItems: ITiedItems = [
            ...newData.labeledOrPlaceholderItems[tiedItemsIndex].filter(
              item => item.dndType !== ItemType.PlaceholderItem,
            ),
            newLabeledItem,
          ];
          if (newTiedItems.length < maxTiedItems) {
            newTiedItems.push(PlaceholderItem());
          }

          newData.labeledOrPlaceholderItems.splice(tiedItemsIndex, 1, newTiedItems);

          for (let i = 0; i < newData.labeledOrPlaceholderItems.length; i++) {
            newData.labeledOrPlaceholderItems[i] = _.sortBy(newData.labeledOrPlaceholderItems[i], [
              'dndType',
              'id',
            ]);
          }

          return newData;
        });
      }

      // Unlabel item to labeled item
      if (
        dragItem.dndType === ItemType.UnlabeledItem &&
        dropItem.dndType === ItemType.LabeledItem
      ) {
        setData(prevData => {
          const newData = _.cloneDeep(prevData);

          const dragItemPosition = _.findIndex(
            prevData.unlabeledItems,
            item => item.id === dragItem.id,
          );
          assertDefined(dragItemPosition);

          const tiedItemsIndex = _.findIndex(newData.labeledOrPlaceholderItems, tiedItems => {
            return !!_.find(tiedItems, tiedItem => tiedItem.id === dropItem.id);
          });

          const tiedItemIndex = _.findIndex(
            newData.labeledOrPlaceholderItems[tiedItemsIndex],
            tiedItem => {
              return tiedItem.id === dropItem.id;
            },
          );

          const newUnlabeledItem: IUnlabeledItem = {
            ...dropItem,
            dndType: ItemType.UnlabeledItem,
          };

          newData.unlabeledItems.splice(dragItemPosition, 1, newUnlabeledItem);
          newData.labeledOrPlaceholderItems[tiedItemsIndex].splice(tiedItemIndex, 1, {
            ...dragItem,
            dndType: ItemType.LabeledItem,
          });

          for (let i = 0; i < newData.labeledOrPlaceholderItems.length; i++) {
            newData.labeledOrPlaceholderItems[i] = _.sortBy(newData.labeledOrPlaceholderItems[i], [
              'dndType',
              'id',
            ]);
          }

          return newData;
        });
      }

      // Labeled item to placeholder or another labeled item
      if (dragItem.dndType === ItemType.LabeledItem) {
        setData(prevData => {
          const newData = _.cloneDeep(prevData);

          const dragTiedItemsIndex = _.findIndex(
            prevData.labeledOrPlaceholderItems,
            tiedItems => !!_.find(tiedItems, tiedItem => tiedItem.id === dragItem.id),
          );
          const dragTiedItemIndex = _.findIndex(
            newData.labeledOrPlaceholderItems[dragTiedItemsIndex],
            tiedItem => {
              return tiedItem.id === dragItem.id;
            },
          );
          const dropTiedItemsIndex = _.findIndex(
            prevData.labeledOrPlaceholderItems,
            tiedItems => !!_.find(tiedItems, tiedItem => tiedItem.id === dropItem.id),
          );
          const dropTiedItemIndex = _.findIndex(
            newData.labeledOrPlaceholderItems[dropTiedItemsIndex],
            tiedItem => {
              return tiedItem.id === dropItem.id;
            },
          );

          newData.labeledOrPlaceholderItems[dragTiedItemsIndex].splice(dragTiedItemIndex, 1, {
            ...dropItem,
          });
          newData.labeledOrPlaceholderItems[dropTiedItemsIndex].splice(dropTiedItemIndex, 1, {
            ...dragItem,
          });

          // clean up
          let i = 0;
          for (const tiedItems of newData.labeledOrPlaceholderItems) {
            // add placeholder if needed
            if (
              !_.find(tiedItems, tiedItem => tiedItem.dndType === ItemType.PlaceholderItem) &&
              tiedItems.length < maxTiedItems
            ) {
              tiedItems.push(PlaceholderItem());
            }

            // remove placeholder if needed
            if (
              tiedItems.length > 0 &&
              tiedItems.filter(tiedItem => tiedItem.dndType === ItemType.PlaceholderItem).length > 1
            ) {
              newData.labeledOrPlaceholderItems[i] = [
                ...tiedItems.filter(item => item.dndType !== ItemType.PlaceholderItem),
                PlaceholderItem(),
              ];
            }
            i++;
          }

          for (let i = 0; i < newData.labeledOrPlaceholderItems.length; i++) {
            newData.labeledOrPlaceholderItems[i] = _.sortBy(newData.labeledOrPlaceholderItems[i], [
              'dndType',
              'id',
            ]);
          }

          return newData;
        });
      }
    },
    [],
  );

  const onDeleteTiedItems = useCallback((tiedItems: ITiedItems) => {
    setData(prevData => {
      const newData = _.cloneDeep(prevData);

      const labeledItemIndex = _.findIndex(newData.labeledOrPlaceholderItems, x =>
        _.isEqual(x, tiedItems),
      );
      assertDefined(labeledItemIndex);

      newData.labeledOrPlaceholderItems.splice(labeledItemIndex, 1, [PlaceholderItem()]);

      for (const tiedItem of tiedItems) {
        if (tiedItem.dndType === ItemType.LabeledItem) {
          newData.unlabeledItems.push({
            ...tiedItem,
            dndType: ItemType.UnlabeledItem,
          });
        }
      }

      return newData;
    });
  }, []);

  const { unlabeledItems, labeledOrPlaceholderItems } = data;

  return {
    unlabeledItems,
    labeledOrPlaceholderItems,
    onDropToLabeledOrPlaceholderItem,
    onDeleteTiedItems,
  };
};

function _getInitialRankingOrderData(
  numOptions: number,
  numItemsToRank: number,
  maxTiedItems: number,
  optionLabel: string,
  annotation: RankingOrderFieldAnnotation,
  itemsToRankCandidates?: number[],
  _isReviewMode?: boolean,
): IRankingOrderData {
  const response = annotation.response;
  let unlabeledItems: IUnlabeledItem[] = _.range(numOptions)
    .filter(index => !_.flatten(response.map(x => x.split(','))).includes(index.toString()))
    .map(index => {
      return {
        id: index.toString(),
        itemLabel: optionLabel,
        dndType: ItemType.UnlabeledItem,
      };
    });
  let labeledOrPlaceholderItems: ILabelOrPlaceholderItems = _.range(0, numItemsToRank).map(() => {
    return [PlaceholderItem()];
  });
  response.forEach((item, index) => {
    if (item === '' || item === 'null') {
      return;
    }

    const tiedItems = item.split(',');

    labeledOrPlaceholderItems[index] = tiedItems.map(tiedItem => {
      return {
        id: tiedItem,
        itemLabel: optionLabel,
        dndType: ItemType.LabeledItem,
      };
    });
    if (labeledOrPlaceholderItems[index].length < maxTiedItems) {
      labeledOrPlaceholderItems[index].push(PlaceholderItem());
    }
  });
  // worker need to rerank
  if (itemsToRankCandidates && itemsToRankCandidates.length > 0) {
    unlabeledItems = itemsToRankCandidates.map(index => {
      return {
        id: index.toString(),
        itemLabel: optionLabel,
        dndType: ItemType.UnlabeledItem,
      };
    });
    unlabeledItems = unlabeledItems.filter(item => itemsToRankCandidates.includes(Number(item.id)));
    labeledOrPlaceholderItems = itemsToRankCandidates.map(() => {
      return [PlaceholderItem()];
    });
  }
  return { unlabeledItems, labeledOrPlaceholderItems };
}

function toAnnotationResponse(data: IRankingOrderData, mapping?: number[]): string[] {
  const response = data.labeledOrPlaceholderItems.map(tiedItem => {
    const labeledItems = tiedItem.filter(item => item.dndType === ItemType.LabeledItem);
    if (labeledItems.length === 0) return 'null';
    return labeledItems.map(item => item.id).join(',');
  });
  if (mapping?.length && !data.unlabeledItems.length) {
    return response.map(r => `${mapping[parseInt(r, 10)] - 1}`);
  }
  return response;
}

// Placeholder builder
function PlaceholderItem(): IPlaceholderItem {
  return {
    id: uuid.v4(),
    dndType: ItemType.PlaceholderItem,
  };
}
